import { useMemo, type FC } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { Delete } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { Button, Card } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import BaseSelect from '../../components/BaseSelect.jsx';
import CardHeader from '../../components/CardHeader.jsx';
import SelectWithSearch from '../../components/SelectWithSearch.jsx';
import {
  EntityTypesEnum,
  getMapLabelByEntityType,
  getMapValueFieldLabelByEntityType,
} from '../../constants/singleFlow.js';
import type { OptionItem } from '../../types/shared.js';
import {
  type ApproverItemInput,
  type FlowFormInput,
} from '../../types/singleFlow.js';
import OperationSection from './OperationSection.jsx';

type Props = {
  index: number;
  item: ApproverItemInput & { id: string };
  allUsers: OptionItem[];
  allRoles: OptionItem[];
  isFlowInUse: boolean;
  onRemove: (groupIndex: number) => void;
};

const ApproverGroup: FC<Props> = ({
  index,
  item,
  allUsers,
  allRoles,
  isFlowInUse,
  onRemove,
}) => {
  const { t } = useTranslation('@myn/approval_v2');

  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const { control, setValue, formState, trigger } =
    useFormContext<FlowFormInput>();
  const [approvers, requesterType, requesterValue] = useWatch({
    control,
    name: ['approvers', 'requesterType', 'requesterValue'],
  });

  const mapLabelByEntityType = getMapLabelByEntityType(t);
  const entityOptions = [
    {
      value: EntityTypesEnum.enum.user,
      label: mapLabelByEntityType[EntityTypesEnum.enum.user],
    },
    {
      value: EntityTypesEnum.enum.role,
      label: mapLabelByEntityType[EntityTypesEnum.enum.role],
    },
  ];

  const mapValueFieldLabelByEntityType = getMapValueFieldLabelByEntityType(t);

  const mapExcludedEntitiesByEntityType = useMemo(() => {
    const result = {
      [EntityTypesEnum.enum.role]: new Set<string>(),
      [EntityTypesEnum.enum.user]: new Set<string>(),
    };

    const requesterUserList =
      requesterType === EntityTypesEnum.enum.user ? requesterValue : [];

    requesterUserList.forEach((requesterItem) => {
      result[EntityTypesEnum.enum.user].add(requesterItem.value);
    });

    const approverEntityList = [
      ...approvers.slice(0, index),
      ...approvers.slice(index + 1),
    ];

    for (const item of approverEntityList) {
      if (!item.entityId || !item.type) {
        continue;
      }

      result[item.type].add(item.entityId);
    }

    return result;
  }, [approvers, requesterType, requesterValue, index]);

  const isRemoveGroupButtonDisabled = isReadOnly || isFlowInUse;

  const getValueFieldMeta = () => {
    if (!item.type) {
      return {
        label: '',
        options: [],
      };
    }

    const excludedSet = mapExcludedEntitiesByEntityType[item.type];
    const mapAllItemListByEntityType = {
      [EntityTypesEnum.enum.role]: allRoles,
      [EntityTypesEnum.enum.user]: allUsers,
    };
    const allItemList = mapAllItemListByEntityType[item.type];
    const filteredOptions = allItemList.filter(
      (item) => !excludedSet.has(item.value),
    );

    return {
      label: mapValueFieldLabelByEntityType[item.type],
      options: filteredOptions,
    };
  };

  const valueFieldMeta = getValueFieldMeta();

  return (
    <div>
      {item.operation ? <OperationSection index={index} /> : null}
      <Card
        key={item.id}
        variant="default"
        className={cn(
          {
            'mt-4': !!item.operation,
            'lg:w-1/2': !item.type,
          },
          'w-full',
        )}
        hover="noHover"
      >
        <div className="flex justify-between gap-2">
          <CardHeader
            title={t('Approver{{required}}', {
              required: index === 0 ? '*' : '',
            })}
            description={t(
              'Select the role or users to add to this approval group.',
            )}
          />
          {item.operation ? (
            <Button
              aria-label="Remove approver group"
              data-dd-action-name="Remove the group"
              icon
              variant="plain"
              className="size-6"
              type="button"
              onClick={() => onRemove(index)}
              disabled={isRemoveGroupButtonDisabled}
            >
              <Delete
                className={
                  isRemoveGroupButtonDisabled
                    ? 'fill-action-disabled02'
                    : 'fill-error-dark'
                }
              />
            </Button>
          ) : null}
        </div>
        <div
          className={cn({ 'lg:grid-cols-2': !!item.type }, 'mt-4 grid gap-2')}
        >
          <div className="content-start">
            <Controller
              name={`approvers.${index}.type`}
              control={control}
              render={({ field }) => {
                const handleChange = (option: OptionItem) => {
                  setValue(`approvers.${index}.entityId`, null);
                  setValue(`approvers.${index}.entityLabel`, null);
                  field.onChange(option.value);
                  trigger(`approvers.${index}.entityId`);
                };

                return (
                  <BaseSelect
                    id={`approver-type-field-${index}`}
                    label={t('Approver type')}
                    a11yLabel="Select type of the approver"
                    value={field.value ?? ''}
                    options={entityOptions}
                    onChange={handleChange}
                    error={!!formState.errors.approvers?.[index]?.type?.message}
                    helperText={
                      formState.errors.approvers?.[index]?.type?.message
                    }
                    disabled={isReadOnly}
                  />
                );
              }}
            />
          </div>
          {item.type ? (
            <div className="content-start">
              <Controller
                name={`approvers.${index}.entityId`}
                control={control}
                render={({ field }) => {
                  return (
                    <SelectWithSearch
                      key={item.type}
                      id={`approver-value-field-${index}`}
                      value={field.value ?? ''}
                      label={
                        item.type
                          ? mapValueFieldLabelByEntityType[item.type]
                          : ''
                      }
                      a11yLabel="Select an approver"
                      options={valueFieldMeta.options}
                      onChange={(option) => {
                        setValue(
                          `approvers.${index}.entityLabel`,
                          option.label,
                        );
                        field.onChange(option.value);
                      }}
                      error={
                        !!formState.errors.approvers?.[index]?.entityId?.message
                      }
                      helperText={
                        formState.errors.approvers?.[index]?.entityId?.message
                      }
                      disabled={isReadOnly}
                    />
                  );
                }}
              />
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
};

export default ApproverGroup;
